import React, { useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { Archive, AlertTriangle, Calendar, FileText, CheckCircle2, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/Input";

const MOTIFS_FIN_MISSION = [
  "Fin normale de la mission de conseil patrimonial",
  "Résiliation à l'initiative du client",
  "Rupture conventionnelle de mandat",
  "Transfert vers un autre cabinet de gestion de patrimoine",
  "Incompatibilité réglementaire ou KYC non abouti",
  "Autre motif de fin de suivi"
];

export default function ArchiveDossierModal({
  isOpen,
  onClose,
  onConfirm,
  nomDossier = "Dossier",
  reference = "DOS-NC"
}) {
  const [dateFinMission, setDateFinMission] = useState(
    () => new Date().toISOString().split("T")[0]
  );
  const [lettreFinMission, setLettreFinMission] = useState("");
  const [motif, setMotif] = useState(MOTIFS_FIN_MISSION[0]);
  const [notesComplementaires, setNotesComplementaires] = useState("");
  const [isCertified, setIsCertified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const isValid = Boolean(
    dateFinMission &&
    lettreFinMission.trim() &&
    isCertified
  );

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!isValid) {
      setErrorMsg("Veuillez renseigner la date de fin, la lettre de fin de mission et certifier la conformité.");
      return;
    }

    setErrorMsg("");
    setIsSubmitting(true);
    try {
      await onConfirm({
        dateFinMission,
        lettreFinMission: lettreFinMission.trim(),
        motif,
        notes: notesComplementaires.trim()
      });
      onClose();
    } catch (err) {
      console.error("Erreur lors de l'archivage:", err);
      setErrorMsg(err?.response?.data?.message || "Une erreur est survenue lors de l'archivage.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setErrorMsg("");
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog static open={isOpen} onClose={handleClose} className="relative z-[100]">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            aria-hidden="true"
          />

          <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
            <DialogPanel
              as={motion.div}
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              className="relative bg-surface rounded-2xl shadow-2xl border border-workspace p-6 w-full max-w-lg overflow-hidden"
            >
              {/* Bouton de fermeture */}
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="absolute top-4 right-4 p-1 text-subtle hover:text-primary rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* En-tête réglementaire */}
              <div className="flex items-start gap-4 mb-5">
                <div className="shrink-0 p-3 rounded-xl bg-amber-500/10 text-amber-600 border border-amber-500/20">
                  <Archive className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <DialogTitle className="text-lg font-bold text-primary">
                      Archivage Réglementaire du Dossier
                    </DialogTitle>
                  </div>
                  <p className="text-xs text-subtle mt-0.5">
                    {nomDossier} • <span className="font-mono">{reference}</span>
                  </p>
                </div>
              </div>

              {/* Alerte informative CIF / AMF */}
              <div className="mb-5 p-3.5 rounded-xl bg-surface-muted/60 border border-workspace text-xs text-secondary flex items-start gap-2.5">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-semibold text-primary">Conformité réglementaire CIF / AMF</p>
                  <p className="text-subtle leading-relaxed">
                    L'archivage d'un client clôture la convention d'accompagnement. Il est obligatoire de formaliser une lettre de fin de mission et de renseigner la date de clôture effective.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Champ 1 : Date de fin de mission */}
                <div>
                  <label className="block text-xs font-semibold text-primary mb-1.5 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-muted" />
                    <span>Date de fin de mission <span className="text-danger-600">*</span></span>
                  </label>
                  <Input
                    type="date"
                    required
                    value={dateFinMission}
                    onChange={(e) => setDateFinMission(e.target.value)}
                    className="w-full text-xs"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Champ 2 : Lettre de fin de mission (justificatif / réf) */}
                <div>
                  <label className="block text-xs font-semibold text-primary mb-1.5 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5 text-muted" />
                    <span>Réf. ou Justificatif de la Lettre de Fin de Mission <span className="text-danger-600">*</span></span>
                  </label>
                  <Input
                    type="text"
                    required
                    placeholder="Ex : LFM-2026-042 ou Lettre_Fin_Mission_Signee.pdf"
                    value={lettreFinMission}
                    onChange={(e) => setLettreFinMission(e.target.value)}
                    className="w-full text-xs"
                    disabled={isSubmitting}
                  />
                  <span className="text-[10px] text-subtle mt-1 block">
                    Référence archivistique ou intitulé de la pièce signée versée aux documents.
                  </span>
                </div>

                {/* Champ 3 : Motif de fin de suivi */}
                <div>
                  <label className="block text-xs font-semibold text-primary mb-1.5">
                    Motif de fin de suivi
                  </label>
                  <select
                    value={motif}
                    onChange={(e) => setMotif(e.target.value)}
                    disabled={isSubmitting}
                    className="flex h-11 w-full rounded-[var(--hp-radius-md)] border border-[var(--hp-color-border)] bg-[var(--hp-color-surface)] px-3 py-2 text-xs text-[var(--hp-color-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hp-color-focus)]"
                  >
                    {MOTIFS_FIN_MISSION.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>

                {/* Champ 4 : Notes internes / détails */}
                <div>
                  <label className="block text-xs font-semibold text-primary mb-1.5">
                    Notes complémentaires (optionnel)
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Commentaires internes ou informations sur la restitution des documents..."
                    value={notesComplementaires}
                    onChange={(e) => setNotesComplementaires(e.target.value)}
                    disabled={isSubmitting}
                    className="w-full rounded-[var(--hp-radius-md)] border border-[var(--hp-color-border)] bg-[var(--hp-color-surface)] p-2.5 text-xs text-[var(--hp-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--hp-color-focus)]"
                  />
                </div>

                {/* Case de certification réglementaire */}
                <div className="pt-2">
                  <label className="flex items-start gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={isCertified}
                      onChange={(e) => setIsCertified(e.target.checked)}
                      disabled={isSubmitting}
                      className="mt-0.5 rounded border-tertiary text-[var(--hp-color-accent)] focus:ring-[var(--hp-color-focus)] h-4 w-4"
                    />
                    <span className="text-xs text-secondary leading-snug">
                      Je certifie qu'une <strong>lettre de fin de mission</strong> a été notifiée et que le dossier sera conservé conformément aux obligations d'archivage réglementaire.
                    </span>
                  </label>
                </div>

                {/* Message d'erreur */}
                {errorMsg && (
                  <div className="p-2.5 rounded-lg bg-danger-50 text-danger-700 text-xs border border-danger-200">
                    {errorMsg}
                  </div>
                )}

                {/* Boutons d'action */}
                <div className="mt-6 pt-4 border-t border-workspace flex justify-end items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Annuler
                  </Button>

                  <Button
                    type="submit"
                    variant="accent"
                    size="sm"
                    disabled={!isValid || isSubmitting}
                    className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white border-transparent"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {isSubmitting ? "Archivage en cours..." : "Confirmer l'archivage"}
                  </Button>
                </div>
              </form>
            </DialogPanel>
          </div>
        </Dialog>
      )}
    </AnimatePresence>
  );
}
